import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  // All pages inside AppShell (persistent sidebar/topbar)
  {
    path: '/',
    component: () => import('@/components/layout/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/public/HomeView.vue'),
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/public/RegisterView.vue'),
      },
      {
        path: 'organizers',
        name: 'organizers',
        component: () => import('@/views/public/OrganizersView.vue'),
      },

      // Account routes
      {
        path: 'account',
        component: () => import('@/views/account/AccountLayout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'account',
            component: () => import('@/views/account/EventsView.vue'),
          },
          {
            path: 'settings',
            component: () => import('@/views/account/SettingsLayout.vue'),
            children: [
              {
                path: '',
                name: 'account-settings',
                component: () => import('@/views/account/SettingsView.vue'),
              },
              {
                path: 'referrals',
                name: 'account-referrals',
                component: () => import('@/views/account/ReferralsView.vue'),
              },
              {
                path: 'notifications',
                name: 'account-notifications',
                component: () => import('@/views/account/NotificationsView.vue'),
              },
            ],
          },
        ],
      },

      // Org selection
      {
        path: 'orgs',
        name: 'orgs',
        component: () => import('@/views/org/OrgsListView.vue'),
        meta: { requiresAuth: true },
      },

      // Org dashboard (OrgContextWrapper handles auth + data)
      {
        path: 'org/:orgSlug',
        component: () => import('@/views/org/OrgContextWrapper.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'org-events',
            component: () => import('@/views/org/OrgEventsView.vue'),
          },
          {
            path: 'members',
            name: 'org-members',
            component: () => import('@/views/org/OrgMembersView.vue'),
          },
          {
            path: 'registrations',
            name: 'org-registrations',
            component: () => import('@/views/org/OrgRegistrationsView.vue'),
          },
          {
            path: 'scan',
            name: 'org-scan',
            component: () => import('@/views/org/OrgScanView.vue'),
          },
          {
            path: 'notifications',
            name: 'org-notifications',
            component: () => import('@/views/org/OrgNotificationsView.vue'),
          },
          {
            path: 'venues',
            name: 'org-venues',
            component: () => import('@/views/org/OrgVenuesView.vue'),
          },
          {
            path: 'settings',
            name: 'org-settings',
            component: () => import('@/views/org/OrgSettingsView.vue'),
          },
        ],
      },

      // Public event page: /:orgSlug/:eventSlug
      {
        path: ':orgSlug/:eventSlug',
        name: 'event',
        component: () => import('@/views/public/RegisterView.vue'),
      },

      // Admin routes
      {
        path: 'admin',
        meta: { requiresAuth: true, requiresPlatformAdmin: true },
        children: [
          {
            path: '',
            name: 'admin-dashboard',
            component: () => import('@/views/admin/DashboardView.vue'),
          },
          {
            path: 'events',
            name: 'admin-events',
            component: () => import('@/views/admin/EventsView.vue'),
          },
          {
            path: 'registrations',
            name: 'admin-registrations',
            component: () => import('@/views/admin/RegistrationsView.vue'),
          },
          {
            path: 'sales',
            name: 'admin-sales',
            component: () => import('@/views/admin/SalesView.vue'),
          },
          {
            path: 'emails',
            name: 'admin-emails',
            component: () => import('@/views/admin/EmailsView.vue'),
          },
          {
            path: 'status',
            name: 'admin-status',
            component: () => import('@/views/admin/StatusView.vue'),
          },
        ],
      },
    ],
  },

  // Standalone pages (no AppShell)
  {
    path: '/confirm',
    name: 'confirm',
    component: () => import('@/views/public/ConfirmationView.vue'),
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import('@/views/public/PaymentView.vue'),
  },
  {
    path: '/staff/:token',
    name: 'staff-scan',
    component: () => import('@/views/public/StaffScanView.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
  },
  {
    path: '/verify',
    name: 'verify',
    component: () => import('@/views/auth/VerifyView.vue'),
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/SignupView.vue'),
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Navigation guard for auth
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  // Check if route requires auth
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Try to refresh token
      const refreshed = await authStore.refresh();
      if (!refreshed) {
        return next({ name: 'login', query: { redirect: to.fullPath } });
      }
    }

    // Check platform admin requirement
    if (to.meta.requiresPlatformAdmin && !authStore.isAdmin) {
      return next({ name: 'home' });
    }
  }

  next();
});

export default router;
